import React from "react";
import Button from "../../button/button";
import { BE_BASE_URL } from "../../../utils/config";
import { useCallback, useState } from "react";
import { BaseCourse, CorsoType, Employee } from "../../../types";
import { axiosInstance } from "../../../provider/authProvider";
import { messaggioDiErrore } from "../../../features/notifiche/avvisi";
import { useEffect } from "react";
import { Select, SelectItem, SelectProvider } from "../../form/select";
import { Color } from "../../../constants/color";
import { CircleX, XCircle } from "lucide-react";
import styled from "styled-components";
import Label from "../../label";
import * as Ariakit from "@ariakit/react";
import ResetCss from "../../ResetCss";
import { AdvisedCoursesExtractor, AdvisedCourse } from "../../../types";
import { BarLoader } from "react-spinners";
import { toast, ToastContainer, Bounce } from "react-toastify";
import { useTranslation } from "react-i18next";
import Suggerimento from "../../tooltip/suggerimento";

const selectNominationsStyle = {
  "--padBlock": "0.3rem",
  border: "1px solid var(--blue300)",
  borderRadius: "1rem",
  fontSize: "1rem",
};

const BadgeDiv = styled.div`
  border: 1px solid ${Color.BLUE};
  background-color: ${Color.LIGHT_GREY};
  border-radius: 1rem;
  padding: 4px 8px;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

interface SuccessModalProps {
  isVisible: boolean;
  employee: Employee | null;
  onClose: () => void;
}

const ChooseCourseModal: React.FC<SuccessModalProps> = ({
  isVisible,
  employee,
  onClose,
}) => {
  const { t } = useTranslation();
  const [courses, setCourses] = useState<CorsoType[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<CorsoType[]>([]);
  const [selectedMissingCourse, setSelectedMissingCourse] =
    useState<string>("");
  const [advisedCourses, setAdvisedCourses] =
    useState<AdvisedCoursesExtractor | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchCourses = useCallback(async () => {
    try {
      let url = `${BE_BASE_URL}/employee/${employee?.id}/suitedCourses`;
      const response = await axiosInstance.get<
        Omit<BaseCourse, "variationId">[]
      >(url);
      setCourses(response.data);
    } catch (e) {
      toast.error(
        messaggioDiErrore(e, "Errore durante il recupero dei documenti."),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        }
      );
    }
  }, [employee]);

  const fetchAdvisedCourses = async () => {
    setLoading(true);
    try {
      const url = `${BE_BASE_URL}/employee/${employee?.id}/advisedCourses`;
      const response = await axiosInstance.get<AdvisedCoursesExtractor>(url);
      setAdvisedCourses(response.data);
      //console.log(response.data);
    } catch (e) {
      toast.error(
        messaggioDiErrore(e, "Errore durante il recupero dei documenti."),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVisible) {
      fetchCourses();
      fetchAdvisedCourses();
    }
  }, [isVisible, fetchCourses]);

  const handleRemoveCourse = async (courseId: number) => {
    setSelectedCourse((prev) => prev.filter((c) => c.id !== courseId));
  };

  const courseStore = Ariakit.useSelectStore({
    value: selectedMissingCourse,
    setValue: (value) => {
      setSelectedMissingCourse(value);
      if (value) {
        const selectedOption = courses.find((c) => c.name === value);
        if (selectedOption) {
          setSelectedCourse((prev) => [
            ...prev,
            {
              id: selectedOption.id,
              name: selectedOption.name,
              code: selectedOption.code,
            },
          ]);
          setSelectedMissingCourse("");
        }
      }
    },
  });

  const renderCourseValue = (value: string | string[]) => {
    if (Array.isArray(value)) {
      const selectedOptions = courses.filter((option) =>
        value.includes(option.name)
      );
      return selectedOptions.length > 0
        ? selectedOptions.map((option) => option.name).join(", ")
        : "Seleziona un corso dal tuo abbonamento...";
    } else {
      const selectedOption = courses.find((option) => option.name === value);
      return selectedOption
        ? selectedOption.name
        : "Seleziona una corso dal tuo abbonamento...";
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData();
    selectedCourse.forEach((course) => {
      formData.append("ids", course.id.toString());
    });
    if (employee?.id) {
      formData.append("employeeId", employee.id.toString());
    } else {
      toast.info("L'ID dell'impiegato è nullo e non può essere inviato.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
      return;
    }
    try {
      const response = await axiosInstance.patch(
        `${BE_BASE_URL}/courses-missing`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.status === 200 || response.status === 201) {
        toast.success("Corsi assegnati con successo al dipendente!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        });
        onClose();
        setSelectedCourse([]);
        setSelectedMissingCourse("");
        setAdvisedCourses(null);
      }
    } catch (e) {
      toast.error(messaggioDiErrore(e, "Errore durante l'assegnazione dei corsi."), {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  return (
    <>
      <ResetCss>
        <Ariakit.Dialog
          open={isVisible}
          onClose={() => {
            setSelectedCourse([]);
            setSelectedMissingCourse("");
            setAdvisedCourses(null);
            onClose();
          }}
          className="dialog"
          style={{
            height: "70vh",
            margin: "0 auto",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              overflowY: "auto",
            }}
          >
            <div
              style={{
                flexGrow: 1,
              }}
            >
              {/* HEADER TITOLO DIALOG */}
              <Ariakit.DialogHeading className="heading" render={<h2 />}>
                {/* Senza dipendente scelto l'intestazione restava vuota, e con lei il
                    nome della finestra. */}
                {employee
                  ? `${employee.lastName} ${employee.firstName}`
                  : "Corsi del dipendente"}
                <Ariakit.DialogDismiss
                  aria-label="Chiudi"
                  style={{
                    position: "absolute",
                    backgroundColor: "transparent",
                    border: "none",
                    top: "calc(50% - 12px)",
                    right: ".5em",
                    cursor: "pointer",
                  }}
                >
                  <XCircle size={24} color={Color.GREEN} />
                </Ariakit.DialogDismiss>
              </Ariakit.DialogHeading>
              {/* CORPO DIALOG Suggerimenti AI */}
              <div
                style={{
                  padding: ".8rem 2rem",
                  overflow: "auto",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "1.2rem",
                    color: Color.BLUE,
                  }}
                >
                  È possibile assegnare i corsi necessari a{" "}
                  {employee?.firstName}
                  <br />
                  Verifica di aver caricato gli eventuali attestati del
                  dipendente.
                </p>
                <div style={{ paddingBottom: "1.5rem" }}>
                  <h2 style={{ color: Color.BLUE, fontFamily: "inherit" }}>
                    Corsi Consigliati
                  </h2>
                  <div
                    style={{
                      maxHeight: "30vh",
                      overflow: "auto",
                      paddingRight: ".5rem",
                    }}
                  >
                    {loading ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "start",
                          gap: ".2rem",
                          flexDirection: "column",
                          fontSize: "1.5rem",
                          height: "20vh",
                        }}
                      >
                        <p>Caricamento...</p>
                        <BarLoader width="20ch" loading={loading} />
                      </div>
                    ) : (
                      <ul
                        style={{
                          color: Color.BLACK,
                          fontFamily: "inherit",
                          padding: 0,
                          margin: 0,
                          listStyleType: "none",
                        }}
                      >
                        {advisedCourses?.advisedCourses.map((course, index) => (
                          <li key={index}>
                            <span style={{ color: Color.BLUE }}>
                              {course.title}
                            </span>
                            : {course.reason}
                            {/* Verifica se il corso non è parte dell'abbonamento */}
                            {!course.isSubscribed && (
                              <span style={{ color: "red" }}>
                                {" "}
                                (Il corso non fa parte del proprio abbonamento)
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* FORM con select Corsi + bottone submit */}
            <form
              onSubmit={handleSubmit}
              style={{
                padding: "1rem 2rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <p
                  style={{
                    color: Color.BLUE,
                    margin: "0", // Rimuovi il margine
                    padding: "0", // Rimuovi il padding
                    lineHeight: "1.2", // Imposta un line-height più basso se necessario
                  }}
                >
                  Per procedere, selezionare i corsi di interesse dal menù a
                  tendina. Se non fosse necessario o si volesse fare
                  successivamente, chiudere la finestra.
                </p>
                <SelectProvider store={courseStore}>
                  <Select
                    style={selectNominationsStyle}
                    aria-label={t("employee-registry-corso-del-dipendente")}
                    name="course"
                    renderValue={renderCourseValue}
                    value={selectedMissingCourse}
                    onChange={(e) => {
                      const target = e.target as HTMLSelectElement;
                      const value = target.value;
                      courseStore.setValue(value);
                    }}
                    modal
                  >
                    {courses.map((course) => (
                      <SelectItem
                        key={course.id}
                        value={course.name}
                        disabled={selectedCourse.some(
                          (c) => c.name === course.name
                        )}
                      >
                        {course.name}
                      </SelectItem>
                    ))}
                  </Select>
                </SelectProvider>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: ".5rem",
                  flexWrap: "wrap",
                  paddingTop: "1.5rem",
                }}
              >
                {selectedCourse.map((course) => (
                  <BadgeDiv key={course.id}>
                    <Label
                      color={Color.DARK_BLUE}
                      text={course.name}
                      fontSize={"14px"}
                    />
                    <button
                      type="button"
                      aria-label={`Togli il corso ${course.name}`}
                      onClick={() => handleRemoveCourse(course.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: "red",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      <CircleX width={16} height={16} />
                    </button>
                  </BadgeDiv>
                ))}
              </div>
              <span
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignContent: "center",
                  justifyContent: "end",
                  padding: "1rem 0",
                }}
              >
                {selectedCourse.length > 0 && (
                  <Suggerimento testo={t("employee-modal-aiuto-assegna-corsi")}>
                    <Button
                      type="submit"
                      label="Assegna ai Corsi"
                      color={Color.GREEN}
                    />
                  </Suggerimento>
                )}
              </span>
            </form>
          </div>
        </Ariakit.Dialog>
      </ResetCss>
    </>
  );
};

export default ChooseCourseModal;
